import * as React from 'react';

import { Button, Col, Row } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideContainer,
  LucideDot,
  LucideFileOutput,
  LucidePlusCircle,
  LucideShip,
  LucideTrash2,
} from 'lucide-react';

import { ObjectListEntry } from '../_state/_types';
import * as conversationState from '../_state/conversation.state';
import * as conversationListState from '../_state/conversationList.state';
import { $selectedConversationObjectsById } from '../_state/objects.state';

import { ContentBox } from './ContentBox';
import { NewObjectPopover } from './NewObjectPopover';
import { withStatusColorStyle } from './withStatusColorStyle';

export type ObjectRowProps = {
  object: ObjectListEntry;
  onDelete: (object: ObjectListEntry) => void;
};

export const ObjectRow = ({ object, onDelete }: ObjectRowProps) => (
  <Row
    key={object.id}
    className="@ObjectRow rounded-lg p-1 pr-2.5 text-sm hover:bg-neutral/5 hover:[&_button]:opacity-100"
    gap2
    style={withStatusColorStyle(object.status)}
  >
    <Row
      className={
        'size-11 rounded bg-custom-color/10 text-custom-color [&_svg]:size-4'
      }
      xCenter
    >
      {object.type === 'load' ? (
        <LucideContainer />
      ) : object.type === 'salesorder' ? (
        <LucideFileOutput />
      ) : (
        <LucideShip />
      )}
    </Row>
    <Col>
      <Row>
        <span className={'font-semibold'}>{object.title}</span>
        <LucideDot className={'size-4 opacity-40'} />
        <span className={'text-custom-color'}>{object.status}</span>
      </Row>
      <Row className={'text-current/50'}>{object.subtitle || '–'}</Row>
    </Col>
    <Button
      icon={<LucideTrash2 />}
      variant="outline"
      color="failure"
      size="icon"
      className={'ml-auto opacity-20'}
      onClick={() => onDelete(object)}
    />
  </Row>
);

export const TabObjects = () => {
  const selectedConversation = useAtomValue(
    conversationListState.$selectedConversation,
  );

  const deleteObject = useSetAtom(conversationState.$deleteObject);

  const selectedConversationObjectsById = useAtomValue(
    $selectedConversationObjectsById,
  );

  const linkedObjects = React.useMemo(
    () =>
      (selectedConversation?.custom.objectsData ?? []).map((it) => ({
        ...it,
        ...selectedConversationObjectsById[it.id],
      })),
    [selectedConversation?.custom.objectsData, selectedConversationObjectsById],
  );

  const salesOrderList = React.useMemo(
    () => linkedObjects.filter((it) => it.type === 'salesorder') ?? [],
    [linkedObjects],
  );

  const bookingList = React.useMemo(
    () => linkedObjects.filter((it) => it.type === 'booking') ?? [],
    [linkedObjects],
  );

  const loadList = React.useMemo(
    () => linkedObjects.filter((it) => it.type === 'load') ?? [],
    [linkedObjects],
  );

  const handleDelete = React.useCallback(
    (object: ObjectListEntry) => {
      deleteObject(object.id);
    },
    [deleteObject],
  );

  return (
    <ContentBox className="@TabObjects p-1">
      <Col gap2 className={'h-full'}>
        <Row
          className={'p-1.5 pb-0 pl-2 text-sm text-current/50 uppercase'}
          gap2
        >
          <span>Linked Objects</span>
          <span className={'rounded bg-current/15 px-1.5 py-px'}>
            {linkedObjects.length}
          </span>
          <NewObjectPopover>
            <Button
              icon={<LucidePlusCircle />}
              color="primary"
              className={'ml-auto h-auto! px-3 py-1.5'}
              size="small"
            >
              Add Object
            </Button>
          </NewObjectPopover>
        </Row>
        <Col
          className={
            'h-full overflow-y-auto px-2 [&_hr]:my-1.5 [&_hr]:border-t [&_hr]:border-b-0 [&_hr]:border-neutral/10'
          }
        >
          {salesOrderList.map((it) => (
            <ObjectRow key={it.id} object={it} onDelete={handleDelete} />
          ))}
          {!!bookingList.length && (
            <>
              {!!salesOrderList.length && <hr />}
              {bookingList.map((it) => (
                <ObjectRow key={it.id} object={it} onDelete={handleDelete} />
              ))}
            </>
          )}
          {!!loadList.length && (
            <>
              {(!!bookingList.length || !!salesOrderList.length) && <hr />}
              {loadList.map((it) => (
                <ObjectRow key={it.id} object={it} onDelete={handleDelete} />
              ))}
            </>
          )}
        </Col>
      </Col>
    </ContentBox>
  );
};
